import React from "react";
import { useCurrentFrame, useVideoConfig, interpolate, spring } from "remotion";

export const OceanBackground: React.FC = () => {
  const frame = useCurrentFrame();
  const { width, height, fps } = useVideoConfig();

  // --- Caustic light ripple pulses ---
  const pulse1 = spring({
    frame: frame % 90,
    fps,
    config: { damping: 12, stiffness: 60, mass: 1.2 },
  });
  const pulse2 = spring({
    frame: (frame + 30) % 90,
    fps,
    config: { damping: 14, stiffness: 55, mass: 1.4 },
  });
  const pulse3 = spring({
    frame: (frame + 60) % 90,
    fps,
    config: { damping: 10, stiffness: 65, mass: 1.0 },
  });

  const causticScale1 = interpolate(pulse1, [0, 1], [0.92, 1.08]);
  const causticScale2 = interpolate(pulse2, [0, 1], [0.95, 1.05]);
  const causticScale3 = interpolate(pulse3, [0, 1], [0.88, 1.12]);

  // Slow drift positions for caustics
  const driftX1 = Math.sin(frame * 0.008) * 60;
  const driftY1 = Math.cos(frame * 0.006) * 40;
  const driftX2 = Math.cos(frame * 0.007 + 1.5) * 70;
  const driftY2 = Math.sin(frame * 0.009 + 0.8) * 50;
  const driftX3 = Math.sin(frame * 0.005 + 2.8) * 50;
  const driftY3 = Math.cos(frame * 0.008 + 1.2) * 60;

  // --- Bubble definitions (10 bubbles, deterministic) ---
  const bubbles = [
    { x: 0.08, size: 6, speed: 280, swayAmp: 18, swayFreq: 0.018, delay: 0 },
    { x: 0.18, size: 10, speed: 310, swayAmp: 24, swayFreq: 0.014, delay: 40 },
    { x: 0.3, size: 5, speed: 260, swayAmp: 14, swayFreq: 0.022, delay: 80 },
    { x: 0.42, size: 12, speed: 290, swayAmp: 30, swayFreq: 0.016, delay: 20 },
    { x: 0.55, size: 8, speed: 300, swayAmp: 20, swayFreq: 0.019, delay: 110 },
    { x: 0.63, size: 4, speed: 270, swayAmp: 12, swayFreq: 0.025, delay: 60 },
    { x: 0.72, size: 11, speed: 320, swayAmp: 28, swayFreq: 0.012, delay: 150 },
    { x: 0.8, size: 7, speed: 285, swayAmp: 22, swayFreq: 0.02, delay: 30 },
    { x: 0.88, size: 9, speed: 295, swayAmp: 16, swayFreq: 0.017, delay: 95 },
    { x: 0.95, size: 5, speed: 275, swayAmp: 10, swayFreq: 0.023, delay: 70 },
  ];

  return (
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "#010d1a",
        overflow: "hidden",
        zIndex: 0,
      }}
    >
      {/* === Caustic Light Ripple 1 === */}
      <div
        style={{
          position: "absolute",
          left: `${width * 0.25 + driftX1}px`,
          top: `${height * 0.3 + driftY1}px`,
          width: "500px",
          height: "500px",
          transform: `translate(-50%, -50%) scale(${causticScale1})`,
          background:
            "radial-gradient(circle, rgba(0,212,255,0.09) 0%, rgba(127,255,212,0.06) 40%, transparent 70%)",
          borderRadius: "50%",
          pointerEvents: "none",
        }}
      />

      {/* === Caustic Light Ripple 2 === */}
      <div
        style={{
          position: "absolute",
          left: `${width * 0.72 + driftX2}px`,
          top: `${height * 0.45 + driftY2}px`,
          width: "620px",
          height: "620px",
          transform: `translate(-50%, -50%) scale(${causticScale2})`,
          background:
            "radial-gradient(circle, rgba(127,255,212,0.08) 0%, rgba(0,212,255,0.05) 45%, transparent 70%)",
          borderRadius: "50%",
          pointerEvents: "none",
        }}
      />

      {/* === Caustic Light Ripple 3 === */}
      <div
        style={{
          position: "absolute",
          left: `${width * 0.5 + driftX3}px`,
          top: `${height * 0.65 + driftY3}px`,
          width: "550px",
          height: "550px",
          transform: `translate(-50%, -50%) scale(${causticScale3})`,
          background:
            "radial-gradient(circle, rgba(0,95,138,0.10) 0%, rgba(0,212,255,0.06) 50%, transparent 72%)",
          borderRadius: "50%",
          pointerEvents: "none",
        }}
      />

      {/* === Rising Bubble Particles === */}
      {bubbles.map((b, i) => {
        const cycleFrame = (frame + b.delay) % b.speed;
        const rawY = interpolate(
          cycleFrame,
          [0, b.speed],
          [height + 20, -b.size - 20],
        );
        const sway = Math.sin(frame * b.swayFreq + i * 0.9) * b.swayAmp;
        const bubbleX = b.x * width + sway;
        const fadeIn = interpolate(cycleFrame, [0, 30], [0, 1], {
          extrapolateLeft: "clamp",
          extrapolateRight: "clamp",
        });
        const fadeOut = interpolate(
          cycleFrame,
          [b.speed - 30, b.speed],
          [1, 0],
          {
            extrapolateLeft: "clamp",
            extrapolateRight: "clamp",
          },
        );

        return (
          <div
            key={i}
            style={{
              position: "absolute",
              left: `${bubbleX}px`,
              top: `${rawY}px`,
              width: `${b.size}px`,
              height: `${b.size}px`,
              borderRadius: "50%",
              background: "rgba(0,212,255,0.25)",
              boxShadow: `0 0 ${b.size * 1.5}px rgba(0,212,255,0.15)`,
              opacity: Math.min(fadeIn, fadeOut),
              pointerEvents: "none",
            }}
          />
        );
      })}

      {/* === Deep Blue Gradient Overlay === */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          background:
            "linear-gradient(180deg, #010d1a 0%, #003d6b 50%, #010d1a 100%)",
          opacity: 0.7,
          pointerEvents: "none",
        }}
      />

      {/* === Aqua Bioluminescent Glow at Bottom === */}
      <div
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          width: "100%",
          height: "500px",
          background:
            "radial-gradient(ellipse at 50% 100%, rgba(0,212,255,0.08) 0%, transparent 60%)",
          pointerEvents: "none",
        }}
      />
    </div>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
